import { useState } from 'react';
import { Mail, Copy, Check, Loader2, Link2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { resendSessionInvite } from '@/services/apiClient';
import { toast } from 'sonner';

/**
 * Resending an invitation, and the link to share by hand.
 *
 * An invite can go astray for reasons invisible from here — a spam filter, a
 * typo in the address, an SMTP hiccup. Until now the only recovery was to
 * cancel and rebook, which changes the link and confuses the candidate further.
 *
 * The copyable URL matters as much as the resend: when email is the thing that
 * is broken, sending the link over WhatsApp is what actually gets the interview
 * done.
 */

const fmt = (d) => d
    ? new Date(d).toLocaleString('en-IN', {
        day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true,
    })
    : null;

export default function ResendInvitePanel({ session, onSent }) {
    const [sending, setSending] = useState(false);
    const [copied, setCopied] = useState(false);
    const [resetPassword, setResetPassword] = useState(true);
    const [result, setResult] = useState(null);

    const url = session?.uniqueLink
        ? `https://app.echohire.in/login?redirect=/instructions/${session.uniqueLink}`
        : null;

    // A finished or dead interview cannot be re-entered, so offering a resend
    // would only send a link that fails.
    const closed = ['completed', 'terminated', 'expired', 'cancelled'].includes(session?.status);

    const sentAt = fmt(session?.inviteSentAt);
    const resends = session?.inviteResendCount || 0;

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error('Could not copy. Select the link and copy it manually.');
        }
    };

    const send = async () => {
        setSending(true);
        try {
            const res = await resendSessionInvite(session._id, resetPassword);
            toast.success(res.message);
            setResult(res.data);
            onSent?.(res.data);
        } catch (err) {
            toast.error(err.message || 'Could not resend the invitation.');
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="space-y-3 rounded-lg border border-border bg-muted/20 p-3">
            <div className="flex items-center justify-between gap-2">
                <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    <Mail className="h-3.5 w-3.5" /> Invitation
                </h4>
                <span className="text-[11px] text-muted-foreground">
                    {sentAt
                        ? `Sent ${sentAt}${resends > 0 ? ` · resent ${resends}×` : ''}`
                        : 'Never sent'}
                </span>
            </div>

            {/* An unsent invite is the likeliest reason a candidate has not shown up */}
            {!sentAt && !closed && (
                <p className="flex items-start gap-1.5 text-[11px] text-amber-600 dark:text-amber-500">
                    <AlertTriangle className="mt-px h-3 w-3 shrink-0" />
                    <span>No invitation has gone out for this interview yet.</span>
                </p>
            )}

            {url && (
                <div>
                    <label className="text-[11px] font-medium text-muted-foreground">
                        Candidate link — share directly if email is not reaching them
                    </label>
                    <div className="mt-1 flex items-center gap-1.5">
                        <code className="flex-1 truncate rounded border border-border bg-background px-2 py-1.5 font-mono text-[11px] text-foreground">
                            {url}
                        </code>
                        <Button size="sm" variant="outline" className="h-7 shrink-0 gap-1 text-xs" onClick={copy}>
                            {copied ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
                            {copied ? 'Copied' : 'Copy'}
                        </Button>
                    </div>
                </div>
            )}

            {!closed && (
                <>
                    <label className="flex cursor-pointer items-start gap-2">
                        <input
                            type="checkbox"
                            checked={resetPassword}
                            onChange={e => setResetPassword(e.target.checked)}
                            className="mt-0.5 accent-teal-500"
                        />
                        <span className="text-[11px] leading-relaxed text-muted-foreground">
                            Issue new sign-in details. Recommended — it guarantees the credentials in
                            the email work. Any password the candidate already set stops working.
                        </span>
                    </label>

                    <Button size="sm" className="w-full gap-1.5" disabled={sending} onClick={send}>
                        {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
                        {sentAt ? 'Resend invitation' : 'Send invitation'}
                    </Button>
                </>
            )}

            {closed && (
                <p className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
                    <Link2 className="mt-px h-3 w-3 shrink-0" />
                    <span>
                        This interview is {session.status}, so the link can no longer be used.
                        Reschedule to send a fresh invitation.
                    </span>
                </p>
            )}

            {result?.passwordReset && (
                <p className="rounded border border-emerald-500/30 bg-emerald-500/5 px-2 py-1.5 text-[11px] text-emerald-700 dark:text-emerald-400">
                    New sign-in details sent to {result.sentTo}
                    {result.recordsUpdated > 1 && (
                        <> — applied to all {result.recordsUpdated} of their accounts, so the
                        password works whichever one they sign in with</>
                    )}.
                </p>
            )}

            {/* A staff account on the same address wins at login, so nothing we
                set on the candidate account can be used. */}
            {result?.shadowedByStaffAccount && (
                <p className="flex items-start gap-1.5 rounded border border-destructive/30 bg-destructive/10 px-2 py-1.5 text-[11px] text-destructive">
                    <AlertTriangle className="mt-px h-3 w-3 shrink-0" />
                    <span>
                        This address also has a staff account, which takes priority at sign-in.
                        The candidate cannot log in with these details until that is sorted —
                        send them the link above instead.
                    </span>
                </p>
            )}
        </div>
    );
}
